import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Plus, Users, UserPlus, Trash2, Edit2, X, Search, UserCheck, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const AttendanceTeams = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [teamName, setTeamName] = useState('');
  const [selectedLeader, setSelectedLeader] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [teamsRes, usersRes] = await Promise.all([
        api.getAttendanceTeams(),
        api.getUsers()
      ]);
      setTeams(teamsRes.data);
      setUsers(usersRes.data.filter(u => u.role !== 'admin'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim() || !selectedLeader) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.createAttendanceTeam({
        team_name: teamName,
        team_leader_id: selectedLeader
      });
      toast.success('Team created successfully');
      setShowCreateModal(false);
      setTeamName('');
      setSelectedLeader('');
      fetchData();
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    try {
      await api.deleteAttendanceTeam(teamId);
      toast.success('Team deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const openMembersModal = (team) => {
    setSelectedTeam(team);
    setSelectedMembers(team.member_ids || []);
    setShowMembersModal(true);
  };

  const handleUpdateMembers = async () => {
    if (!selectedTeam) return;

    try {
      await api.updateTeamMembers(selectedTeam.id, selectedMembers);
      toast.success('Team members updated');
      setShowMembersModal(false);
      fetchData();
    } catch (error) {
      console.error('Error updating members:', error);
      toast.error(error.response?.data?.detail || 'Failed to update members');
    }
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getAvailableUsersForLeader = () => {
    // Get users who are not already team leaders
    const leaderIds = teams.map(t => t.team_leader_id);
    return users.filter(u => !leaderIds.includes(u.id));
  };

  const getAvailableMembers = () => {
    // Filter users based on search and exclude current team leader
    const leaderIds = teams.map(t => t.team_leader_id);
    return users
      .filter(u => !leaderIds.includes(u.id))
      .filter(u => 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance Teams</h1>
          <p className="text-slate-500 mt-1">Manage teams and assign team leaders for attendance marking</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          data-testid="create-team-btn"
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Team
        </Button>
      </div>

      {/* Teams Grid */}
      {teams.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Teams Yet</h3>
              <p className="text-slate-500 mb-4">Create your first attendance team to get started</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <Card key={team.id} className="hover:shadow-lg transition-shadow" data-testid={`team-card-${team.id}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{team.team_name}</CardTitle>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openMembersModal(team)}
                      data-testid={`edit-members-${team.id}`}
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteTeam(team.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-team-${team.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg">
                    <UserCheck className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-xs text-slate-500">Team Leader</p>
                      <p className="font-medium text-slate-900">{team.team_leader_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{team.member_count || 0} members</span>
                  </div>

                  {team.members && team.members.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-2">Members:</p>
                      <div className="flex flex-wrap gap-1">
                        {team.members.slice(0, 5).map(member => (
                          <span 
                            key={member.id}
                            className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-700"
                          >
                            {member.name}
                          </span>
                        ))}
                        {team.members.length > 5 && (
                          <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-500">
                            +{team.members.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Create New Team</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Team Name *
                  </label>
                  <Input
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter team name"
                    data-testid="team-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Team Leader *
                  </label>
                  <select
                    value={selectedLeader}
                    onChange={(e) => setSelectedLeader(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="team-leader-select"
                  >
                    <option value="">Select a team leader</option>
                    {getAvailableUsersForLeader().map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>

                {getAvailableUsersForLeader().length === 0 && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">All users are already team leaders</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateTeam}
                  disabled={!teamName.trim() || !selectedLeader}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="confirm-create-team"
                >
                  Create Team
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Members Modal */}
      {showMembersModal && selectedTeam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Manage Team Members</h2>
                  <p className="text-sm text-slate-500">{selectedTeam.team_name}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowMembersModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search users..."
                    className="pl-10"
                    data-testid="search-members-input"
                  />
                </div>
              </div>

              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {getAvailableMembers().length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    No users found
                  </div>
                ) : (
                  getAvailableMembers().map(user => (
                    <label 
                      key={user.id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(user.id)}
                        onChange={() => toggleMember(user.id)}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <div>
                        <p className="font-medium text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email} • {user.role}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>

              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  Selected: <span className="font-medium">{selectedMembers.length} members</span>
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowMembersModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateMembers}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-members-btn"
                >
                  Save Members
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceTeams;
